"use client";

// ── Reading a submitted program ──────────────────────────────────────────────
// The view `EX-PUB17` built for a moderator (line numbers, wrap, copy, the
// language named), lifted here so the problem page's Submissions tab
// (`EX-SRC01`, ADR-0082) and a published round's field list show a program the
// same way. It takes its words as `labels`, so each surface passes strings from
// its own message layer instead of this file reaching into one.
//
// **The source is never reformatted, and that is deliberate.** A submission is
// evidence: it is what the similarity scope fingerprinted (`docs/29` §6.1) and
// what an authorship dispute is argued over (`EX-AUT06`). Prettifying it here
// would mean the reader and the record disagree about what was submitted, so
// everything below is presentation around bytes left exactly as they arrived.
//
// `EX-SCB04` (Wave 24 decision 7) colours it, in the Judge editor's palette,
// and nothing else: `program-highlighter.ts` is fetched with `import()` once a
// program is on screen, and its runs are drawn only when they join back to
// exactly `source`. Until then, for a language the runner does not have, or
// past `PROGRAM_HIGHLIGHT_MAX_CHARS`, the program shows plain. Copy always
// writes `source` itself. `progsity-admin`'s `components/code/program-source-view.tsx`
// is the same view.
//
// `EX-CMP01` adds `marks`, for two programs read side by side: the program is
// then drawn a line at a time, each line of a matched region washed in that
// region's colour (the same colour on the other program), the active region
// stronger, and the names that were renamed marked inside it. The bytes are
// still the bytes (`program-marks.ts` cuts without adding or dropping one).

import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Copy, WrapText } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { ProgramRun } from "./program-highlighter";
import { segmentProgramLines } from "./program-marks";
import styles from "./program-code.module.css";

export type ProgramSourceLabels = {
  /** "42 lines", already counted by the caller. */
  lines: string;
  wrap: string;
  copy: string;
  copied: string;
};

/** How many region colours there are; region `n` wears colour `n % PROGRAM_MARK_TONES`. */
export const PROGRAM_MARK_TONES = 6;

export type ProgramRegionMark = {
  /** 1-based, inclusive. */
  startLine: number;
  endLine: number;
  /** Which colour, `0` to `PROGRAM_MARK_TONES - 1`; the same on both programs. */
  tone: number;
  active: boolean;
};

export type ProgramMarks = {
  regions: readonly ProgramRegionMark[];
  /** Renamed names as `[start, end)` offsets into `source`. */
  renamed: ReadonlyArray<readonly [number, number]>;
  /** What a renamed name says on hover ("Renamed"). */
  renamedLabel: string;
  /** The line to bring into view, 1-based; it changes as the reader moves between matches. */
  focusLine: number | null;
};

const TONE_CLASS = [styles.tone0, styles.tone1, styles.tone2, styles.tone3, styles.tone4, styles.tone5];

/** The program a line at a time, with the regions and renamed names marked. */
function MarkedLines({
  source,
  runs,
  marks,
  wrap,
}: {
  source: string;
  runs: ProgramRun[] | null;
  marks: ProgramMarks;
  wrap: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Cut once per program; a step between matches only moves which region is active.
  const lines = useMemo(() => segmentProgramLines(source, runs, marks.renamed), [source, runs, marks.renamed]);
  const regionOfLine = useMemo(() => {
    const byLine = new Map<number, ProgramRegionMark>();
    for (const region of marks.regions) {
      for (let line = region.startLine; line <= region.endLine; line += 1) {
        if (!byLine.has(line) || region.active) byLine.set(line, region);
      }
    }
    return byLine;
  }, [marks.regions]);

  useEffect(() => {
    const frame = scrollRef.current;
    if (!frame || marks.focusLine === null) return;
    const row = frame.querySelector<HTMLElement>(`[data-line="${marks.focusLine}"]`);
    if (!row) return;
    frame.scrollTop = Math.max(0, row.offsetTop - frame.clientHeight / 4);
  }, [marks.focusLine]);

  return (
    <div ref={scrollRef} className="relative max-h-[min(70vh,640px)] overflow-auto" data-testid="program-marked">
      <div
        data-colored={runs ? "true" : "false"}
        className={cn(styles.code, "min-w-max py-3 font-mono text-[12px] leading-relaxed [tab-size:4]", wrap && "min-w-0")}
      >
        {lines.map((segments, index) => {
          const line = index + 1;
          const region = regionOfLine.get(line);
          return (
            <div
              key={line}
              data-line={line}
              data-tone={region ? region.tone : undefined}
              data-active={region?.active ? "true" : undefined}
              className={cn(
                "flex",
                styles.markedLine,
                region ? TONE_CLASS[region.tone % PROGRAM_MARK_TONES] : null,
                region?.active ? styles.activeLine : null,
              )}
            >
              <span aria-hidden className={cn(styles.gutter, "sticky left-0 w-11 shrink-0 select-none pr-3 text-right")}>
                {line}
              </span>
              <span className={cn("min-w-0 flex-1 pl-3 pr-3", wrap ? "whitespace-pre-wrap break-words" : "whitespace-pre")}>
                {segments.map((segment, at) => {
                  const text = segment.className ? (
                    <span key={at} className={segment.className}>
                      {segment.text}
                    </span>
                  ) : (
                    segment.text
                  );
                  return segment.renamed ? (
                    <mark key={at} className={styles.renamed} title={marks.renamedLabel} data-renamed="true">
                      {text}
                    </mark>
                  ) : (
                    text
                  );
                })}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The coloured runs of `source`, or `null` while they load and whenever it shows plain. */
function useProgramRuns(source: string, languageKey: string | null | undefined): ProgramRun[] | null {
  const [colored, setColored] = useState<{ source: string; languageKey: string; runs: ProgramRun[] } | null>(null);

  useEffect(() => {
    if (!languageKey) return;
    let live = true;
    import("./program-highlighter")
      .then(({ highlightProgram }) => {
        const runs = highlightProgram(source, languageKey);
        if (live) setColored(runs ? { source, languageKey, runs } : null);
      })
      .catch(() => {
        // The chunk did not load: the program stays plain, which is still the program.
      });
    return () => {
      live = false;
    };
  }, [source, languageKey]);

  return colored && colored.source === source && colored.languageKey === languageKey ? colored.runs : null;
}

export function ProgramSourceView({
  source,
  languageKey,
  languageName,
  languageVersion,
  labels,
  marks,
}: {
  source: string;
  /** The runner's language id (`cpp`, `python`): what the program is coloured as. */
  languageKey?: string | null;
  /** The name printed instead of the id ("C++"); the id is printed when it is absent. */
  languageName?: string | null;
  languageVersion?: string | null;
  labels: ProgramSourceLabels;
  /** `EX-CMP01`: matched regions and renamed names, for a program read beside another. */
  marks?: ProgramMarks;
}) {
  const [wrap, setWrap] = useState(false);
  const [copied, setCopied] = useState(false);
  const runs = useProgramRuns(source, languageKey);

  const lines = source.split("\n");
  const languageLabel = languageName || languageKey;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(source);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard refused - the text is still selectable by hand.
    }
  };

  // shrink-0: the submission sheets put this in a scrolling flex column, where the
  // overflow-hidden frame would otherwise shrink to the leftover height and cut the
  // program's last lines off. Outside a flex column it does nothing.
  return (
    <div className="shrink-0 overflow-hidden rounded-xl border border-white/10 bg-neutral-950">
      <div className="flex flex-wrap items-center gap-2 border-b border-white/10 px-3 py-2">
        {languageLabel ? (
          <span className="rounded-md bg-white/10 px-2 py-0.5 font-mono text-[11px] text-white/80">
            {languageLabel}
            {languageVersion ? ` ${languageVersion}` : ""}
          </span>
        ) : null}
        <span className="font-mono text-[11px] text-white/45">{labels.lines}</span>

        <div className="ml-auto flex items-center gap-1">
          <Button
            type="button"
            variant="ghostDark"
            size="sm"
            onClick={() => setWrap((value) => !value)}
            aria-pressed={wrap}
          >
            <WrapText className="h-3.5 w-3.5" aria-hidden />
            {labels.wrap}
          </Button>
          <Button type="button" variant="ghostDark" size="sm" onClick={() => void copy()}>
            {copied ? (
              <Check className="h-3.5 w-3.5 text-emerald-400" aria-hidden />
            ) : (
              <Copy className="h-3.5 w-3.5" aria-hidden />
            )}
            {copied ? labels.copied : labels.copy}
          </Button>
        </div>
      </div>

      {marks ? (
        <MarkedLines source={source} runs={runs} marks={marks} wrap={wrap} />
      ) : (
        // Two columns rather than numbers inside the text: the gutter has to stay
        // put when a long line scrolls sideways, and it must never come along when
        // somebody selects the program to paste it somewhere.
        <div className="flex max-h-[420px] overflow-auto">
          <div
            aria-hidden
            className="shrink-0 select-none border-r border-white/10 bg-black/20 px-2 py-3 text-right font-mono text-[12px] leading-relaxed text-white/25"
          >
            {lines.map((_line, index) => (
              <div key={index}>{index + 1}</div>
            ))}
          </div>
          <pre
            data-colored={runs ? "true" : "false"}
            className={cn(
              styles.code,
              "min-w-0 flex-1 px-3 py-3 font-mono text-[12px] leading-relaxed [tab-size:4]",
              wrap ? "whitespace-pre-wrap break-words" : "whitespace-pre",
            )}
          >
            {runs
              ? runs.map((run, index) =>
                  run.className ? (
                    <span key={index} className={run.className}>
                      {run.text}
                    </span>
                  ) : (
                    run.text
                  ),
                )
              : source}
          </pre>
        </div>
      )}
    </div>
  );
}
